import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/lib/brand";
import {
  VideoCamera,
  CalendarPlus,
  PlayCircle,
  CheckCircle,
  Circle,
  Sparkle,
  Users,
  CaretDown,
} from "@phosphor-icons/react";

// Iter35d — Community-lite (Phase 1). Two sections: Upcoming events + Replay library.
// Iter36 update — Replay library gets a type filter + 3x3 pagination + tightened
// eyebrow rules (money-date = Money Date; everything else = Session).

function formatWhen(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function joinWindowOpen(iso, durationMinutes) {
  if (!iso) return false;
  const start = new Date(iso).getTime();
  const end = start + (durationMinutes || 30) * 60_000;
  const now = Date.now();
  return now >= start - 15 * 60_000 && now <= end + 15 * 60_000;
}

// One tidy rule for the eyebrow on both upcoming cards and replay cards:
//   money-date → "Money Date"
//   weekly     → "Weekly ritual"
//   anything else (incl. one-off / guest / training) → "Session"
function eyebrowFor(kind) {
  if (kind === "money-date") return "Money Date";
  if (kind === "weekly") return "Weekly ritual";
  return "Session";
}

const REPLAY_PAGE_SIZE = 9;
const UPCOMING_PER_MONTH_VISIBLE = 4;

// Iter38 — group upcoming events by calendar month so users can plan
// ahead. Each month collapses to `UPCOMING_PER_MONTH_VISIBLE` cards with
// a "Show more" toggle. Backend now returns 12 weeks of events so the
// second/third month has real content once the schedule fills up.
function groupByMonth(events) {
  const groups = new Map();
  for (const e of events) {
    if (!e?.starts_at) continue;
    const d = new Date(e.starts_at);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const label = d.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
    if (!groups.has(key)) groups.set(key, { key, label, events: [] });
    groups.get(key).events.push(e);
  }
  return Array.from(groups.values()).sort((a, b) => a.key.localeCompare(b.key));
}

export default function CommunityPage() {
  const [upcoming, setUpcoming] = useState([]);
  const [replays, setReplays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replayFilter, setReplayFilter] = useState("all"); // "all" | "money-date" | "session"
  const [replayLimit, setReplayLimit] = useState(REPLAY_PAGE_SIZE);
  // Iter38 — per-month "expanded" set. When a month key is in the set, we
  // render all its events; otherwise we collapse to UPCOMING_PER_MONTH_VISIBLE.
  const [expandedMonths, setExpandedMonths] = useState(new Set());

  const load = async () => {
    setLoading(true);
    try {
      const [u, r] = await Promise.all([
        axios.get(`${API}/events/upcoming`),
        axios.get(`${API}/events/replays`),
      ]);
      setUpcoming(u.data.events || []);
      setReplays(r.data.events || []);
    } catch (e) {
      toast.error("Could not load community events");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const toggleRsvp = async (eventId) => {
    try {
      const res = await axios.post(`${API}/events/${eventId}/rsvp`);
      toast.success(res.data.rsvped ? "You're in — see you there!" : "RSVP removed");
      load();
    } catch (e) {
      toast.error("Could not update RSVP");
    }
  };

  const downloadIcs = (eventId) => {
    window.open(`${API}/events/${eventId}/ics`, "_blank");
  };

  // Apply replay type filter. "session" = anything that isn't a money-date.
  const filteredReplays = useMemo(() => {
    if (replayFilter === "all") return replays;
    if (replayFilter === "money-date") return replays.filter((e) => e.kind === "money-date");
    return replays.filter((e) => e.kind !== "money-date");
  }, [replays, replayFilter]);

  const visibleReplays = filteredReplays.slice(0, replayLimit);

  // Reset pagination when the filter changes
  useEffect(() => {
    setReplayLimit(REPLAY_PAGE_SIZE);
  }, [replayFilter]);

  return (
    <div className="space-y-8" data-testid="community-page">
      <div>
        <div className="section-eyebrow">Community</div>
        <h1 className="font-heading uppercase text-4xl md:text-5xl tracking-wide mt-4 leading-[1.05]">
          You're not figuring this out alone.
        </h1>
        <p className="mt-3 text-black/75 leading-relaxed">
          Join our <strong>Monday check-ins</strong> (15-30 minutes, weekly, drop-in) to ask
          questions, log your numbers, troubleshoot or just come for a chat. And on the first
          Friday of every month, we turn logging your actuals from a chore into a shared ritual:
          the <strong>Money Date</strong> (60 minutes, deeper Q&A + community vibes). Bring your
          coffee, bring your questions, bring your wins. Plus regular live webinars to sharpen
          your money skills on cashflow topics that truly matter. All sessions are on Zoom —
          intimate, informal, and refreshingly non-corporate.
        </p>
      </div>

      {/* Upcoming events — Iter38 grouped by calendar month with a rolling
          12-week window (backend cutoff). Each month collapses to
          UPCOMING_PER_MONTH_VISIBLE cards with a "Show more" toggle. */}
      <section data-testid="community-upcoming">
        <h2 className="font-heading uppercase text-2xl flex items-center gap-2 mb-4">
          <VideoCamera size={22} weight="duotone" /> Upcoming events
        </h2>
        {loading && upcoming.length === 0 ? (
          <p className="text-sm text-black/55">Loading…</p>
        ) : upcoming.length === 0 ? (
          <div className="card text-center py-10">
            <p className="text-black/60">No upcoming sessions on the schedule right now — check back soon.</p>
          </div>
        ) : (
          <div className="space-y-8">
            {groupByMonth(upcoming).map((month) => {
              const expanded = expandedMonths.has(month.key);
              const visible = expanded
                ? month.events
                : month.events.slice(0, UPCOMING_PER_MONTH_VISIBLE);
              const hidden = month.events.length - visible.length;
              return (
                <div key={month.key} data-testid={`upcoming-month-${month.key}`}>
                  <div className="section-eyebrow mb-3">{month.label}</div>
                  <div className="grid md:grid-cols-2 gap-4">
                    {visible.map((e) => (
                      <div key={e.id} className="card" data-testid={`event-card-${e.id}`}>
                        <div className="section-eyebrow">{eyebrowFor(e.kind)}</div>
                        <h3 className="font-heading uppercase text-lg mt-2">{e.title}</h3>
                        <p className="text-sm text-black/70 mt-2 leading-relaxed">
                          {formatWhen(e.starts_at)} · {e.duration_minutes} min
                        </p>
                        {e.description && (
                          <p className="text-sm text-black/70 mt-3 leading-relaxed whitespace-pre-wrap">
                            {e.description}
                          </p>
                        )}
                        {e.rsvp_count > 0 && (
                          <p className="text-xs text-black/50 mt-3 inline-flex items-center gap-1">
                            <Users size={12} weight="bold" /> {e.rsvp_count} going
                          </p>
                        )}
                        <div className="mt-4 flex flex-wrap gap-2">
                          {joinWindowOpen(e.starts_at, e.duration_minutes) ? (
                            <a
                              href={e.zoom_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn-primary"
                              data-testid={`event-join-${e.id}`}
                            >
                              Join Zoom
                            </a>
                          ) : (
                            <button
                              type="button"
                              onClick={() => toggleRsvp(e.id)}
                              className={e.user_rsvped ? "btn-secondary" : "btn-primary"}
                              data-testid={`event-rsvp-${e.id}`}
                            >
                              {e.user_rsvped ? (
                                <>
                                  <CheckCircle size={14} weight="bold" /> You're in
                                </>
                              ) : (
                                <>
                                  <Circle size={14} weight="bold" /> I'm in
                                </>
                              )}
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => downloadIcs(e.id)}
                            className="btn-secondary"
                            data-testid={`event-ics-${e.id}`}
                          >
                            <CalendarPlus size={14} weight="bold" /> Add to calendar
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  {hidden > 0 && (
                    <div className="flex justify-center mt-4">
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={() =>
                          setExpandedMonths((s) => new Set([...s, month.key]))
                        }
                        data-testid={`show-more-${month.key}`}
                      >
                        Show {hidden} more in {month.label} <CaretDown size={14} weight="bold" />
                      </button>
                    </div>
                  )}
                  {expanded && month.events.length > UPCOMING_PER_MONTH_VISIBLE && (
                    <div className="flex justify-center mt-4">
                      <button
                        type="button"
                        className="text-xs text-black/55 hover:text-black underline"
                        onClick={() =>
                          setExpandedMonths((s) => {
                            const next = new Set(s);
                            next.delete(month.key);
                            return next;
                          })
                        }
                        data-testid={`show-less-${month.key}`}
                      >
                        Show fewer
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Can't make it live? — sits above Replay library */}
      <section className="card-yellow" data-testid="community-cant-make-it">
        <h2 className="font-heading uppercase text-xl">Can't make it live?</h2>
        <p className="mt-2 text-sm text-black/80 leading-relaxed">
          Every Money Date and training session is recorded and added to the replay library within
          48 hours. Catch up on your own time.
        </p>
      </section>

      {/* Replay library */}
      {replays.length > 0 && (
        <section data-testid="community-replays">
          <div className="flex items-baseline justify-between flex-wrap gap-3 mb-4">
            <h2 className="font-heading uppercase text-2xl flex items-center gap-2">
              <PlayCircle size={22} weight="duotone" /> Replay library
            </h2>
            <div className="flex items-center gap-1 border-[1.5px] border-black rounded-lg p-0.5 text-xs" data-testid="replay-filter">
              {[
                { key: "all", label: "All" },
                { key: "money-date", label: "Money Date" },
                { key: "session", label: "Sessions" },
              ].map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setReplayFilter(t.key)}
                  className={`px-3 py-1.5 rounded-md font-heading uppercase tracking-wider transition-all ${
                    replayFilter === t.key
                      ? "bg-[#FFBD4A] text-black"
                      : "bg-transparent text-black/60 hover:text-black hover:bg-[#FFF7E5]"
                  }`}
                  data-testid={`replay-filter-${t.key}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {filteredReplays.length === 0 ? (
            <div className="card text-center py-8">
              <p className="text-black/60 text-sm">No replays in this category yet.</p>
            </div>
          ) : (
            <>
              <div className="grid md:grid-cols-3 gap-4">
                {visibleReplays.map((e) => (
                  <a
                    key={e.id}
                    href={e.recording_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="card hover:shadow-[6px_6px_0_#000] transition-all group"
                    data-testid={`replay-${e.id}`}
                  >
                    <div className="aspect-video bg-black/5 rounded-lg flex items-center justify-center mb-3 group-hover:bg-[#FFBD4A] transition-colors">
                      <PlayCircle size={48} weight="duotone" />
                    </div>
                    <div className="section-eyebrow">{eyebrowFor(e.kind) === "Weekly ritual" ? "Session" : eyebrowFor(e.kind)}</div>
                    <h3 className="font-heading uppercase text-base mt-1">{e.title}</h3>
                    <p className="text-xs text-black/60 mt-1">{formatWhen(e.starts_at)}</p>
                  </a>
                ))}
              </div>

              {replayLimit < filteredReplays.length && (
                <div className="mt-6 text-center">
                  <button
                    type="button"
                    onClick={() => setReplayLimit((n) => n + REPLAY_PAGE_SIZE)}
                    className="btn-secondary"
                    data-testid="replay-show-more"
                  >
                    Show more replays <CaretDown size={14} weight="bold" />
                  </button>
                  <p className="text-xs text-black/50 mt-2">
                    Showing {visibleReplays.length} of {filteredReplays.length}
                  </p>
                </div>
              )}
            </>
          )}
        </section>
      )}

      {/* Wins wall placeholder — Phase 2 */}
      <section className="card-yellow" data-testid="community-wins-teaser">
        <div className="flex items-start gap-3">
          <Sparkle size={24} weight="duotone" className="shrink-0 mt-1" />
          <div>
            <h2 className="font-heading uppercase text-xl">Wins wall · coming soon</h2>
            <p className="text-sm text-black/80 mt-2 leading-relaxed">
              A future home for anonymised weekly wins across the forecastly community — celebrating
              small breakthroughs, big milestones, and everything in between. Opt-in only. Nothing
              shared without your say-so.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
